import type {
  InputHTMLAttributes,
  JSX,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes
} from 'react'
import { useId, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'

/**
 * Поля формы (docs/10 §3.5): лейблы **видимые**, а не placeholder-only;
 * ошибка под полем и связана через `aria-describedby` + `aria-invalid`.
 */

const CONTROL_CLASS =
  'w-full rounded-md border border-border bg-surface-2 px-3 py-2 text-base text-fg ' +
  'placeholder:text-fg-subtle disabled:cursor-not-allowed disabled:opacity-50 ' +
  'aria-[invalid=true]:border-danger'

interface FieldShellProps {
  label: string
  hint?: string
  error?: string | null
  required?: boolean
  children: (ids: { controlId: string; describedBy: string | undefined }) => ReactNode
  className?: string
}

export function FieldShell({
  label,
  hint,
  error,
  required = false,
  children,
  className
}: FieldShellProps): JSX.Element {
  const controlId = useId()
  const hintId = `${controlId}-hint`
  const errorId = `${controlId}-error`
  const describedBy =
    [hint === undefined ? null : hintId, error ? errorId : null].filter(Boolean).join(' ') ||
    undefined

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <label htmlFor={controlId} className="text-sm font-medium text-fg">
        {label}
        {required ? (
          <span aria-hidden className="ml-1 text-primary">
            *
          </span>
        ) : null}
      </label>
      {children({ controlId, describedBy })}
      {hint === undefined ? null : (
        <p id={hintId} className="text-xs text-fg-muted">
          {hint}
        </p>
      )}
      {error ? (
        <p id={errorId} className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string
  hint?: string
  error?: string | null
}

export function TextField({
  label,
  hint,
  error,
  className,
  required,
  ...rest
}: TextFieldProps): JSX.Element {
  return (
    <FieldShell
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
      required={required === true}
    >
      {({ controlId, describedBy }) => (
        <input
          id={controlId}
          className={cn(CONTROL_CLASS, className)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          required={required}
          {...rest}
        />
      )}
    </FieldShell>
  )
}

export function PasswordField({
  label,
  hint,
  error,
  className,
  required,
  ...rest
}: TextFieldProps): JSX.Element {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)

  return (
    <FieldShell
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
      required={required === true}
    >
      {({ controlId, describedBy }) => (
        <div className="relative">
          <input
            id={controlId}
            type={visible ? 'text' : 'password'}
            className={cn(CONTROL_CLASS, 'pr-11', className)}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            required={required}
            {...rest}
          />
          <button
            type="button"
            onClick={() => setVisible(value => !value)}
            aria-label={visible ? t('auth.hidePassword') : t('auth.showPassword')}
            aria-pressed={visible}
            className="absolute top-1/2 right-1 flex size-8 -translate-y-1/2 items-center justify-center rounded-sm text-fg-muted hover:text-fg"
          >
            {visible ? (
              <EyeOff aria-hidden className="size-4" />
            ) : (
              <Eye aria-hidden className="size-4" />
            )}
          </button>
        </div>
      )}
    </FieldShell>
  )
}

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string
  hint?: string
  error?: string | null
  children: ReactNode
}

export function SelectField({
  label,
  hint,
  error,
  className,
  children,
  required,
  ...rest
}: SelectFieldProps): JSX.Element {
  return (
    <FieldShell
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
      required={required === true}
    >
      {({ controlId, describedBy }) => (
        <select
          id={controlId}
          className={cn(CONTROL_CLASS, className)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          required={required}
          {...rest}
        >
          {children}
        </select>
      )}
    </FieldShell>
  )
}

export interface NumberFieldProps extends Omit<TextFieldProps, 'type'> {
  label: string
}

export function NumberField(props: NumberFieldProps): JSX.Element {
  return <TextField {...props} type="number" inputMode="numeric" className="font-mono" />
}

export interface TextAreaFieldProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'id'
> {
  label: string
  hint?: string
  error?: string | null
}

export function TextAreaField({
  label,
  hint,
  error,
  className,
  ...rest
}: TextAreaFieldProps): JSX.Element {
  return (
    <FieldShell
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
    >
      {({ controlId, describedBy }) => (
        <textarea
          id={controlId}
          className={cn(CONTROL_CLASS, 'min-h-20 font-mono', className)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...rest}
        />
      )}
    </FieldShell>
  )
}

export interface SwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
  /** Скрыть подпись визуально (когда рядом уже есть заголовок карточки). */
  hideLabel?: boolean
  disabled?: boolean
  describedBy?: string
}

/** Переключатель на `role="switch"` — читается как «вкл/выкл», а не «чекбокс». */
export function Switch({
  checked,
  onChange,
  label,
  hideLabel = false,
  disabled = false,
  describedBy
}: SwitchProps): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={hideLabel ? label : undefined}
      aria-describedby={describedBy}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="inline-flex items-center gap-3 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span
        aria-hidden
        className={cn(
          'relative h-5 w-9 shrink-0 rounded-full border transition-colors duration-150',
          checked ? 'border-primary bg-primary' : 'border-border-strong bg-surface-3'
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 size-3.5 rounded-full transition-transform duration-150',
            checked ? 'translate-x-4.5 bg-on-primary' : 'translate-x-0.5 bg-fg-muted'
          )}
        />
      </span>
      {hideLabel ? null : <span className="text-base text-fg">{label}</span>}
    </button>
  )
}

export interface CheckboxProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label: ReactNode
  error?: string | null
  disabled?: boolean
}

export function Checkbox({
  checked,
  onChange,
  label,
  error,
  disabled = false
}: CheckboxProps): JSX.Element {
  const id = useId()
  const errorId = `${id}-error`
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start gap-3">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={event => onChange(event.target.checked)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className="mt-0.5 size-4 shrink-0 accent-primary"
        />
        <label htmlFor={id} className="text-base text-fg-muted">
          {label}
        </label>
      </div>
      {error ? (
        <p id={errorId} className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}
